/**
 * Global navigation guard to prevent booking page URL params from being stripped
 *
 * Runs BEFORE navigation to detect and block route changes that would lose
 * critical booking parameters (pitchtype, chargetype).
 *
 * This catches param loss that happens:
 * - During server-side rendering
 * - Before the page component loads
 * - During initial navigation to booking page
 * - During programmatic route updates
 */
export default defineNuxtRouteMiddleware((to, from) => {
  // Skip on server (let server-side validation in useBookingParamValidation handle it)
  if (import.meta.server) {
    return undefined
  }

  // Only guard navigation TO booking pages
  if (!to.path.includes('/book/')) {
    return undefined
  }

  // If we're coming FROM a booking page with params
  // and going TO a booking page without params, block it
  const fromHasPitchtype = from.query?.pitchtype
  const toHasPitchtype = to.query?.pitchtype

  if (from.path.includes('/book/') && fromHasPitchtype && !toHasPitchtype) {
    // Detect bot traffic - don't log bots to Sentry
    const userAgent = navigator?.userAgent || 'unknown'
    const isBot =
      /bot|crawler|spider|crawling|google|bing|yahoo|slurp|duckduckbot|baiduspider|yandex|sogou|exabot|facebot|ia_archiver|Read-Aloud/i.test(
        userAgent,
      )

    // Only log to Sentry for real users (not bots)
    if (!isBot) {
      const { $sentry } = useNuxtApp()

      if ($sentry) {
        // Add context about the navigation attempt
        $sentry.setContext('navigation', {
          fromUrl: from.fullPath,
          toUrl: to.fullPath,
          pitchtypeProtected: fromHasPitchtype,
          chargetypeProtected: from.query?.chargetype || 'none',
        })

        // Capture as error to track attempted param loss
        $sentry.captureException(
          new Error(
            'Booking param loss ATTEMPTED but PREVENTED by navigation guard',
          ),
          {
            tags: {
              page: 'booking',
              guard: 'booking-param-guard',
              prevented: 'true',
            },
            fingerprint: ['booking-param-guard', 'prevented-param-loss'],
          },
        )
      }
    }

    // Abort the bad navigation - stay on current URL with params intact
    return abortNavigation(
      'Navigation blocked: would lose required booking parameters',
    )
  }

  return undefined
})
